import React, { useState, useEffect } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import {
  FiAlertTriangle,
  FiAlertCircle,
  FiXCircle,
  FiSearch,
  FiFilter,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { statusApi } from '../../services/api';
import { SystemError } from '../../types';

const severityConfig = {
  warning: { icon: FiAlertTriangle, color: 'text-amber-600', bg: 'bg-amber-100' },
  error: { icon: FiAlertCircle, color: 'text-orange-600', bg: 'bg-orange-100' },
  fatal: { icon: FiXCircle, color: 'text-red-600', bg: 'bg-red-100' },
};

const ErrorBrowser: React.FC = () => {
  const [errors, setErrors] = useState<SystemError[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [resolvedFilter, setResolvedFilter] = useState<'all' | 'unresolved' | 'resolved'>('unresolved');
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchErrors = async (reset = false) => {
    setIsLoading(true);
    try {
      const currentPage = reset ? 1 : page;
      const response = await statusApi.getErrors({
        severity: severityFilter !== 'all' ? severityFilter : undefined,
        resolved: resolvedFilter === 'all' ? undefined : resolvedFilter === 'resolved',
        limit: 20,
        offset: (currentPage - 1) * 20,
      });

      if (reset) {
        setErrors(response.data);
        setPage(1);
      } else {
        setErrors(prev => [...prev, ...response.data]);
      }
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Failed to fetch errors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors(true);
  }, [severityFilter, resolvedFilter]);

  const handleResolve = async (errorId: string) => {
    try {
      await statusApi.resolveError(errorId);
      setErrors(prev =>
        prev.map(e =>
          e.id === errorId ? { ...e, resolved: true, resolvedAt: new Date().toISOString() } : e
        )
      );
    } catch (error) {
      console.error('Failed to resolve error:', error);
    }
  };

  const filteredErrors = errors.filter(error => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        error.message.toLowerCase().includes(query) ||
        error.errorType.toLowerCase().includes(query) ||
        error.requestPath?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Error Browser</h1>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>{filteredErrors.length} errors</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search errors..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          {/* Severity filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="w-4 h-4 text-gray-400" />
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">All severities</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="fatal">Fatal</option>
            </select>
          </div>

          {/* Resolved filter */}
          <select
            value={resolvedFilter}
            onChange={(e) => setResolvedFilter(e.target.value as typeof resolvedFilter)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="unresolved">Unresolved</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* Error list */}
      <div className="space-y-4">
        {isLoading && errors.length === 0 ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse h-24"></div>
            ))}
          </div>
        ) : filteredErrors.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FiCheck className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No errors found</h3>
            <p className="mt-2 text-gray-500">
              {resolvedFilter === 'unresolved'
                ? 'All errors have been resolved!'
                : 'No errors match your search criteria.'}
            </p>
          </div>
        ) : (
          filteredErrors.map((error) => {
            const config = severityConfig[error.severity];
            const Icon = config.icon;
            const isExpanded = expandedError === error.id;

            return (
              <div
                key={error.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* Error header */}
                <div
                  className={clsx(
                    'p-4 cursor-pointer hover:bg-gray-50 transition-colors',
                    error.resolved && 'opacity-60'
                  )}
                  onClick={() => setExpandedError(isExpanded ? null : error.id)}
                >
                  <div className="flex items-start gap-4">
                    <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', config.bg)}>
                      <Icon className={clsx('w-5 h-5', config.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', config.bg, config.color)}>
                          {error.severity.toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-500">{error.errorType}</span>
                        {error.resolved && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                            Resolved
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-medium text-gray-900 truncate">{error.message}</p>
                      <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                        <span>{formatDistanceToNow(new Date(error.occurredAt), { addSuffix: true })}</span>
                        {error.requestPath && <span>{error.requestPath}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!error.resolved && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolve(error.id);
                          }}
                          className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          Resolve
                        </button>
                      )}
                      {isExpanded ? (
                        <FiChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <FiChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="space-y-4">
                      {/* Metadata */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Occurred At</p>
                          <p className="text-sm font-medium text-gray-900">
                            {format(new Date(error.occurredAt), 'MMM d, yyyy h:mm:ss a')}
                          </p>
                        </div>
                        {error.tenantId && (
                          <div>
                            <p className="text-xs text-gray-500">Tenant ID</p>
                            <p className="text-sm font-medium text-gray-900 font-mono">{error.tenantId}</p>
                          </div>
                        )}
                        {error.userId && (
                          <div>
                            <p className="text-xs text-gray-500">User ID</p>
                            <p className="text-sm font-medium text-gray-900 font-mono">{error.userId}</p>
                          </div>
                        )}
                        {error.userAgent && (
                          <div>
                            <p className="text-xs text-gray-500">User Agent</p>
                            <p className="text-sm text-gray-900 truncate">{error.userAgent}</p>
                          </div>
                        )}
                      </div>

                      {/* Stack trace */}
                      {error.stackTrace && (
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Stack Trace</p>
                          <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto">
                            {error.stackTrace}
                          </pre>
                        </div>
                      )}

                      {/* Context */}
                      {error.context && Object.keys(error.context).length > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Context</p>
                          <pre className="p-4 bg-gray-100 rounded-lg text-xs overflow-x-auto">
                            {JSON.stringify(error.context, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Load more */}
        {hasMore && filteredErrors.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setPage(p => p + 1);
              fetchErrors();
            }}
            disabled={isLoading}
            className="w-full py-3 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Load more errors'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorBrowser;
